import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { SERVICE_GALLERY } from "../data/site";

/**
 * Service gallery carousel. Shows all Trädfällarna work photos on every
 * service page so visitors see the full range of services regardless of
 * which page they landed on.
 *
 * Behaviour:
 * - 1 image visible at a time on mobile, 3 on tablet, 4 on desktop
 * - Arrow buttons scroll one viewport-width at a time (snap)
 * - Hides arrows that lead nowhere (e.g. left arrow when at start)
 * - Dot indicator below shows current position
 * - Keyboard: ←/→ when track is focused
 */
export default function ServiceGallery({
  title = "Bildgalleri från utförda jobb",
  eyebrow = "Galleri",
  intro = "Riktiga uppdrag — trädfällning, beskärning, stubbfräsning, bortforsling och mer i Laholm med omnejd.",
  items = SERVICE_GALLERY,
}) {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [active, setActive] = useState(0);
  // -1 = lightbox closed. Otherwise the active large-image index.
  const [lightbox, setLightbox] = useState(-1);

  const openLightbox = useCallback((i) => setLightbox(i), []);
  const closeLightbox = useCallback(() => setLightbox(-1), []);
  const lightboxPrev = useCallback(
    () => setLightbox((i) => (i <= 0 ? items.length - 1 : i - 1)),
    [items.length]
  );
  const lightboxNext = useCallback(
    () => setLightbox((i) => (i >= items.length - 1 ? 0 : i + 1)),
    [items.length]
  );

  // Lock page scroll + keyboard nav while lightbox is open
  useEffect(() => {
    if (lightbox < 0) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") lightboxPrev();
      else if (e.key === "ArrowRight") lightboxNext();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, closeLightbox, lightboxPrev, lightboxNext]);

  const updateButtons = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    // active dot = floor(scrollLeft / itemWidth)
    const firstCard = el.querySelector("[data-card]");
    if (firstCard) {
      const cardWidth = firstCard.getBoundingClientRect().width + 16; // gap
      setActive(Math.round(el.scrollLeft / cardWidth));
    }
  };

  useEffect(() => {
    updateButtons();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, []);

  const scrollBy = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-card]");
    const step = firstCard
      ? firstCard.getBoundingClientRect().width + 16
      : el.clientWidth * 0.85;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-card]");
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width + 16;
    el.scrollTo({ left: i * cardWidth, behavior: "smooth" });
  };

  const handleKey = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollBy(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollBy(1);
    }
  };

  if (!items.length) return null;

  return (
    <section
      data-testid="service-gallery"
      className="section-pad bg-[#F7F5F0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10 gap-4">
          <div className="max-w-2xl">
            <span className="eyebrow">{eyebrow}</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-tight">
              {title}
            </h2>
            <p className="mt-3 text-[#3a4338] text-base md:text-lg max-w-xl">
              {intro}
            </p>
          </div>

          {/* Arrows */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!canPrev}
              aria-label="Föregående bilder"
              data-testid="service-gallery-prev"
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors ${
                canPrev
                  ? "border-[#1B2A1E] text-[#1B2A1E] hover:bg-[#1B2A1E] hover:text-[#F2A57C]"
                  : "border-[#E2E0D8] text-[#C7C9C3] cursor-not-allowed"
              }`}
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!canNext}
              aria-label="Nästa bilder"
              data-testid="service-gallery-next"
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors ${
                canNext
                  ? "border-[#1B2A1E] text-[#1B2A1E] hover:bg-[#1B2A1E] hover:text-[#F2A57C]"
                  : "border-[#E2E0D8] text-[#C7C9C3] cursor-not-allowed"
              }`}
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          tabIndex={0}
          onKeyDown={handleKey}
          aria-label="Bildgalleri"
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 focus:outline-none focus:ring-2 focus:ring-[#D95D2E]/60 rounded-xl scrollbar-thin"
          style={{ scrollbarWidth: "thin" }}
        >
          {items.map((img, i) => (
            <button
              key={i}
              type="button"
              data-card
              data-testid={`service-gallery-item-${i + 1}`}
              onClick={() => openLightbox(i)}
              aria-label={`Öppna större bild: ${img.caption}`}
              className="relative shrink-0 snap-start w-[78%] sm:w-[44%] lg:w-[31%] xl:w-[23%] tf-img-zoom rounded-xl overflow-hidden bg-[#1B2A1E] shadow-sm cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95D2E] focus-visible:ring-offset-2"
            >
              <img
                src={img.src}
                alt={img.caption}
                loading={i > 1 ? "lazy" : "eager"}
                decoding="async"
                className="w-full aspect-[4/3] object-cover"
              />
            </button>
          ))}
        </div>

        {/* Dot indicators */}
        <div
          className="flex items-center justify-center gap-2 mt-6"
          data-testid="service-gallery-dots"
        >
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Gå till bild ${i + 1}`}
              data-testid={`service-gallery-dot-${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === active
                  ? "w-7 bg-[#D95D2E]"
                  : "w-2 bg-[#C7C9C3] hover:bg-[#1B2A1E]"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox >= 0 && items[lightbox] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Bildvisning"
          data-testid="service-gallery-lightbox"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          {/* Close */}
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Stäng bildvisning"
            data-testid="service-gallery-lightbox-close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md transition-colors"
          >
            <X className="w-5 h-5" strokeWidth={2.4} />
          </button>

          {/* Previous */}
          <button
            type="button"
            onClick={lightboxPrev}
            aria-label="Föregående bild"
            data-testid="service-gallery-lightbox-prev"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md transition-colors"
          >
            <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.4} />
          </button>

          {/* Next */}
          <button
            type="button"
            onClick={lightboxNext}
            aria-label="Nästa bild"
            data-testid="service-gallery-lightbox-next"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md transition-colors"
          >
            <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.4} />
          </button>

          {/* Image */}
          <figure className="relative max-w-[min(92vw,1400px)] max-h-[88vh] mx-4 flex flex-col items-center">
            <img
              key={lightbox}
              src={items[lightbox].src}
              alt={items[lightbox].caption}
              className="max-h-[82vh] w-auto max-w-full object-contain rounded-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            />
            <div className="mt-3 text-white/55 text-xs tabular-nums">
              {lightbox + 1} / {items.length}
            </div>
          </figure>
        </div>
      )}
    </section>
  );
}
